import { memo, useCallback, useMemo } from 'react'

import { ListItemIcon, Menu, MenuItem } from '@mui/material'
import { DeleteOutlineOutlined, GroupOutlined, MoreVertOutlined, VisibilityOutlined } from '@mui/icons-material'

import { IconButton, Tooltip, useDialogState, usePopoverState } from '@collaborate-health/uikit'
import { ContentTrainingMaterialResponse } from '@tabeeb/contracts/microservices/swagger-article-service'

import ContentTrainingMaterialDetachConfirmationDialog from '../ContentTrainingMaterialDetachConfirmationDialog'

export interface ContentTrainingMaterialMenuProps {
  contentTrainingMaterial: ContentTrainingMaterialResponse
  onDetach?: (contentTrainingMaterial: ContentTrainingMaterialResponse) => void
  onView?: (contentTrainingMaterial: ContentTrainingMaterialResponse) => void
  onManageUsers?: (contentTrainingMaterial: ContentTrainingMaterialResponse) => void
}

const ContentTrainingMaterialMenu: React.FC<ContentTrainingMaterialMenuProps> = ({
  contentTrainingMaterial,
  onDetach,
  onManageUsers,
  onView,
}) => {
  const [menuAnchorRef, isMenuOpen, onOpenMenu, onCloseMenu] = usePopoverState<HTMLButtonElement>()
  const [isDetachConfirmationDialogOpen, onOpenDetachConfirmationDialog, onCloseDetachConfirmationDialog] =
    useDialogState()

  const onDetachSubmit = useCallback(() => {
    onCloseDetachConfirmationDialog()
    onDetach?.(contentTrainingMaterial)
  }, [onCloseDetachConfirmationDialog, onDetach, contentTrainingMaterial])

  const options = useMemo(() => {
    const menuOptions = []

    if (onView) {
      menuOptions.push({
        id: 'view',
        title: 'View',
        icon: <VisibilityOutlined />,
        onClick: () => onView(contentTrainingMaterial),
      })
    }

    if (onManageUsers) {
      menuOptions.push({
        id: 'manage-users',
        title: 'Manage users',
        icon: <GroupOutlined />,
        onClick: () => onManageUsers(contentTrainingMaterial),
      })
    }

    if (onDetach) {
      menuOptions.push({
        id: 'detach',
        title: 'Detach',
        icon: <DeleteOutlineOutlined color='error' />,
        onClick: onOpenDetachConfirmationDialog,
      })
    }

    return menuOptions
  }, [onDetach, onManageUsers, onOpenDetachConfirmationDialog, onView, contentTrainingMaterial])

  return (
    <>
      <Tooltip text='Open menu'>
        <IconButton ref={menuAnchorRef} edge='end' size='small' onClick={onOpenMenu}>
          <MoreVertOutlined />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={menuAnchorRef.current}
        open={isMenuOpen}
        onClose={onCloseMenu}
        onClick={onCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {options.map(({ id, title, icon, onClick }) => (
          <MenuItem key={id} title={title} onClick={onClick}>
            <ListItemIcon>{icon}</ListItemIcon>
            {title}
          </MenuItem>
        ))}
      </Menu>

      <ContentTrainingMaterialDetachConfirmationDialog
        open={isDetachConfirmationDialogOpen}
        onClose={onCloseDetachConfirmationDialog}
        onSubmit={onDetachSubmit}
      />
    </>
  )
}

export default memo(ContentTrainingMaterialMenu)

